import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  ShoppingCart,
  Phone,
  MapPin,
  Menu,
  X,
  Heart,
  User,
  Truck,
} from "lucide-react";
import { CATEGORIES, STORE_INFO } from "../mock";
import { useCart } from "../context/CartContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import Logo from "./Logo";

const Header = () => {
  const { count } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    navigate(`/boutique?q=${encodeURIComponent(query)}`);
    setMobileOpen(false);
  };

  return (
    <header className="w-full sticky top-0 z-50 shadow-sm bg-white">
      {/* Top bar */}
      <div className="bg-[#0b2960] text-white text-xs md:text-sm">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="hidden sm:flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" />
              {STORE_INFO.address}
            </span>
            <a href={`tel:${STORE_INFO.phone.replace(/\s/g, "")}`} className="flex items-center gap-1.5 hover:text-red-300 transition-colors">
              <Phone className="h-3.5 w-3.5" />
              {STORE_INFO.phone}
            </a>
          </div>
          <div className="hidden md:flex items-center gap-1.5">
            <Truck className="h-3.5 w-3.5" />
            <span>Livraison rapide à Marseille</span>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="border-b border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="shrink-0" aria-label="EDS ELECTROMENAGER - Accueil">
            <Logo variant="dark" size="md" />
          </Link>

          <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-2xl mx-auto items-center relative">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher un produit, une marque..."
              className="w-full pr-28 h-11 border-2 border-[#0b2960] focus-visible:ring-[#0b2960] rounded-md"
            />
            <Button type="submit" className="absolute right-1 h-9 bg-[#e11d24] hover:bg-[#b8171d] text-white px-4">
              <Search className="h-4 w-4 mr-1" /> Rechercher
            </Button>
          </form>

          <div className="flex items-center gap-1 md:gap-3 ml-auto">
            <Link to="/contact" className="hidden lg:flex flex-col items-center text-xs text-neutral-700 hover:text-[#0b2960] transition-colors px-2">
              <User className="h-5 w-5" />
              <span className="mt-0.5">Contact</span>
            </Link>
            <Link to="/boutique" className="hidden lg:flex flex-col items-center text-xs text-neutral-700 hover:text-[#0b2960] transition-colors px-2">
              <Heart className="h-5 w-5" />
              <span className="mt-0.5">Favoris</span>
            </Link>
            <Link to="/panier" className="relative flex flex-col items-center text-xs text-neutral-700 hover:text-[#0b2960] transition-colors px-2">
              <ShoppingCart className="h-5 w-5" />
              <span className="mt-0.5">Panier</span>
              {count > 0 && (
                <Badge className="absolute -top-1 right-0 bg-[#e11d24] hover:bg-[#e11d24] text-white h-5 min-w-5 px-1 rounded-full text-[10px] flex items-center justify-center">
                  {count}
                </Badge>
              )}
            </Link>
            <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden p-2 text-neutral-800">
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        <form onSubmit={submitSearch} className="md:hidden flex px-4 pb-3 items-center relative">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher..."
            className="w-full pr-24 h-10 border-2 border-[#0b2960]"
          />
          <Button type="submit" className="absolute right-5 h-8 bg-[#e11d24] hover:bg-[#b8171d] text-white px-3">
            <Search className="h-4 w-4" />
          </Button>
        </form>
      </div>

      {/* Category nav */}
      <nav className="hidden md:block bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4">
          <ul className="flex items-center gap-0 overflow-x-auto">
            <li>
              <Link to="/" className="inline-flex items-center px-2.5 py-3 text-[13px] font-semibold text-[#0b2960] hover:text-[#e11d24] transition-colors whitespace-nowrap">
                Accueil
              </Link>
            </li>
            <li>
              <Link to="/neuf" className="inline-flex items-center px-2.5 py-3 text-[13px] font-semibold text-[#0b2960] hover:text-[#e11d24] transition-colors whitespace-nowrap">
                Neuf
              </Link>
            </li>
            <li>
              <Link to="/occasion" className="inline-flex items-center px-2.5 py-3 text-[13px] font-semibold text-[#e11d24] hover:text-[#e11d24] transition-colors whitespace-nowrap">
                Occasion
              </Link>
            </li>
            {CATEGORIES.map((c) => (
              <li key={c.id}>
                <Link
                  to={c.slug === "occasion" ? "/occasion" : `/boutique?cat=${c.slug}`}
                  className={`inline-flex items-center px-2.5 py-3 text-[13px] font-semibold ${c.slug === "occasion" ? "text-[#e11d24]" : "text-neutral-700"} hover:text-[#e11d24] transition-colors whitespace-nowrap`}
                >
                  {c.name}
                </Link>
              </li>
            ))}
            <li className="ml-auto">
              <Link to="/livraison-garantie" className="inline-flex items-center px-2.5 py-3 text-[13px] font-semibold text-neutral-700 hover:text-[#e11d24] transition-colors whitespace-nowrap">
                Livraison & Garantie
              </Link>
            </li>
            <li>
              <Link to="/services" className="inline-flex items-center px-2.5 py-3 text-[13px] font-semibold text-neutral-700 hover:text-[#e11d24] transition-colors whitespace-nowrap">
                Services
              </Link>
            </li>
            <li>
              <Link to="/magasin" className="inline-flex items-center px-2.5 py-3 text-[13px] font-semibold text-neutral-700 hover:text-[#e11d24] transition-colors whitespace-nowrap">
                Magasin
              </Link>
            </li>
            <li>
              <Link to="/contact" className="inline-flex items-center px-2.5 py-3 text-[13px] font-semibold text-neutral-700 hover:text-[#e11d24] transition-colors whitespace-nowrap">
                Contact
              </Link>
            </li>
          </ul>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-neutral-200 shadow-lg">
          <ul className="px-4 py-2 divide-y divide-neutral-100">
            <li>
              <Link to="/" onClick={() => setMobileOpen(false)} className="block py-3 text-sm font-semibold text-neutral-800">Accueil</Link>
            </li>
            {CATEGORIES.map((c) => (
              <li key={c.id}>
                <Link to={c.slug === "occasion" ? "/occasion" : `/boutique?cat=${c.slug}`} onClick={() => setMobileOpen(false)} className={`block py-3 text-sm ${c.slug === "occasion" ? "text-[#e11d24] font-semibold" : "text-neutral-700"}`}>{c.name}</Link>
              </li>
            ))}
            <li>
              <Link to="/services" onClick={() => setMobileOpen(false)} className="block py-3 text-sm text-neutral-700">Services & Dépannage</Link>
            </li>
            <li>
              <Link to="/livraison-garantie" onClick={() => setMobileOpen(false)} className="block py-3 text-sm text-neutral-700">Livraison & Garantie</Link>
            </li>
            <li>
              <Link to="/magasin" onClick={() => setMobileOpen(false)} className="block py-3 text-sm text-neutral-700">Notre magasin</Link>
            </li>
            <li>
              <Link to="/contact" onClick={() => setMobileOpen(false)} className="block py-3 text-sm text-neutral-700">Contact</Link>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
};

export default Header;
